import type { OrchestratorState } from '@demo-portal/shared';
import React, { useState } from 'react';

interface OrchestrationControlsProps {
  orchestratorState: OrchestratorState;
  isProcessing: boolean;
  onReset?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  primaryColor?: string;
}

const OrchestrationControls: React.FC<OrchestrationControlsProps> = ({
  orchestratorState,
  isProcessing,
  onReset,
  onPause,
  onResume,
  primaryColor = '#3498db',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusColor = () => {
    switch (orchestratorState.status) {
      case 'initializing': return '#f39c12';
      case 'discovering': return '#e67e22';
      case 'ready': return '#27ae60';
      case 'processing': return '#3498db';
      case 'synthesizing': return '#9b59b6';
      default: return '#95a5a6';
    }
  };

  const getStatusIcon = () => {
    switch (orchestratorState.status) {
      case 'initializing': return '⚡';
      case 'discovering': return '🔍';
      case 'ready': return '✅';
      case 'processing': return '⚙️';
      case 'synthesizing': return '🧠';
      default: return '💤';
    }
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: 1000,
    }}>
      {/* Expanded Controls */}
      {isExpanded && (
        <div style={{
          background: 'white',
          borderRadius: '15px',
          padding: '15px',
          marginBottom: '10px',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
          border: `2px solid ${primaryColor}`,
          minWidth: '200px',
          animation: 'slideUp 0.3s ease-out',
        }}>
          <div style={{
            fontSize: '1.1em',
            fontWeight: 'bold',
            color: '#2c3e50',
            marginBottom: '10px',
            textAlign: 'center',
          }}>
            Orchestration Controls
          </div>

          {/* Status Display */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            background: `${getStatusColor()}20`,
            padding: '8px 12px',
            borderRadius: '8px',
            marginBottom: '15px',
          }}>
            <span style={{ fontSize: '1.2em' }}>
              {getStatusIcon()}
            </span>
            <span style={{
              fontWeight: 'bold',
              color: getStatusColor(),
              textTransform: 'capitalize',
            }}>
              {orchestratorState.status.replace('_', ' ')}
            </span>
          </div>

          {/* Control Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}>
            {isProcessing && onPause && (
              <button
                onClick={onPause}
                style={{
                  background: '#f39c12',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                ⏸️ Pause
              </button>
            )}

            {!isProcessing && onResume && orchestratorState.status !== 'ready' && (
              <button
                onClick={onResume}
                style={{
                  background: '#27ae60',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                ▶️ Resume
              </button>
            )}

            {onReset && (
              <button
                onClick={onReset}
                style={{
                  background: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                🔄 Reset
              </button>
            )}
          </div>

          {/* System Stats */}
          <div style={{
            marginTop: '15px',
            padding: '10px',
            background: '#f8f9fa',
            borderRadius: '8px',
            fontSize: '0.8em',
            color: '#666',
          }}>
            <div style={{ marginBottom: '4px' }}>
              <strong>Discovered:</strong> {orchestratorState.discoveredAgents.length} agents
            </div>
            <div style={{ marginBottom: '4px' }}>
              <strong>Active:</strong> {orchestratorState.activeAgents.length} agents
            </div>
            <div>
              <strong>Queue:</strong> {orchestratorState.processingQueue.length} steps
            </div>
          </div>
        </div>
      )}

      {/* Main Control Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: isProcessing 
            ? 'linear-gradient(135deg, #3498db, #2980b9)'
            : 'linear-gradient(135deg, #667eea, #764ba2)',
          border: 'none',
          color: 'white',
          fontSize: '1.5em',
          cursor: 'pointer',
          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: isProcessing ? 'pulse 2s infinite' : 'none',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
        }}
      >
        {isExpanded ? '✕' : getStatusIcon()}
      </button>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes pulse {
            0%, 100% {
              box-shadow: 0 4px 15px rgba(0,0,0,0.2);
            }
            50% {
              box-shadow: 0 4px 25px rgba(52, 152, 219, 0.4);
            }
          }
        `}
      </style>
    </div>
  );
};

export default OrchestrationControls;